import { Box, Skeleton, Text } from '@mantine/core';
import { DocumentReferenceContent } from '@medplum/fhirtypes';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { trackDocumentViewed } from '@/services/analytics';
import { getAttachmentBlob } from '@/services/attachments';
import { useMedplum } from '@medplum/react';

type DocumentPreviewProps = {
  content: DocumentReferenceContent;
  height: number | string;
  width: number | string;
  patientId?: string;
  organizationId?: string;
  source?: 'patient timeline' | 'documents pane';
};

export function DocumentPreview({
  content,
  height,
  width,
  patientId,
  organizationId,
  source = 'patient timeline',
}: DocumentPreviewProps): ReactElement | null {
  const medplum = useMedplum();
  const attachment = content?.attachment;
  const hasUrl = Boolean(attachment?.url);
  const isUrlBinary = attachment?.url?.startsWith('Binary/');
  const [hasTracked, setHasTracked] = useState(false);
  const [fileInfo, setFileInfo] = useState<{ url: string; contentType: string } | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  const posthog = usePostHog();

  useEffect(() => {
    const getFileInfo = async () => {
      if (isUrlBinary) {
        try {
          const fileInfo = await getAttachmentBlob(medplum, attachment);
          if (fileInfo) {
            setFileInfo({ url: fileInfo.url, contentType: fileInfo.contentType });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error getting attachment blob:', error);
        } finally {
          setIsLoading(false);
        }
      } else if (hasUrl) {
        setFileInfo({
          url: attachment.url!,
          contentType: attachment.contentType ?? '',
        });
      }

      setIsLoading(false);
    };

    getFileInfo();
  }, [hasUrl, isUrlBinary]);

  const handlePreviewClick = useCallback(() => {
    // Track view only once when user clicks to view
    const resourceId = patientId || organizationId;
    if (hasTracked || !resourceId || !fileInfo?.contentType) {
      return;
    }

    if (fileInfo?.contentType.startsWith('image/') || fileInfo?.contentType === 'application/pdf') {
      trackDocumentViewed(posthog, {
        document_type: fileInfo.contentType,
        source,
        ...(patientId ? { patient_id: patientId } : { organization_id: organizationId }),
      });
      setHasTracked(true);
    }
  }, [hasTracked, patientId, organizationId, fileInfo?.contentType, posthog, source]);

  if (isLoading) {
    return <Skeleton height={height} width={width} />;
  }

  // Image preview
  if (hasUrl && fileInfo?.contentType.startsWith('image/')) {
    return (
      <Box
        onClick={handlePreviewClick}
        style={{ cursor: 'pointer', alignItems: 'center', justifyContent: 'center', display: 'flex' }}
      >
        <img
          src={fileInfo.url}
          alt={attachment?.title || 'Document preview'}
          style={{ maxWidth: width, maxHeight: height, objectFit: 'contain' }}
        />
      </Box>
    );
  }

  // PDF preview
  if (hasUrl && fileInfo?.contentType === 'application/pdf') {
    // Add #toolbar=0 to hide the PDF viewer toolbar (print, download, menu icons)
    const pdfUrl = `${fileInfo?.url}#toolbar=0`;
    return (
      <Box onClick={handlePreviewClick} style={{ cursor: 'pointer' }}>
        <iframe src={pdfUrl} style={{ width, height, border: 'none' }} title="PDF Preview" />
      </Box>
    );
  }

  // Unsupported file - show link
  if (!hasUrl) {
    return null;
  }

  return (
    <Text size="sm" mt="20px" mb="20px">
      Unsupported file. Click download button to access the file.
    </Text>
  );
}
